import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type ThemeMode = "light" | "dark";

/**
 * First-visit default: follow the OS colour scheme. A persisted choice (the
 * header toggle) rehydrates over this, so it only decides the very first
 * render — including the pre-login pages, which share this store.
 */
export function systemThemeMode(): ThemeMode {
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch {
    // jsdom or an exotic browser without matchMedia — assume light.
  }
  return "light";
}

interface UiState {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  onboardingDismissed: boolean;
  dismissOnboarding: () => void;
  /** Route pathnames whose guided tour has already run. */
  completedTours: string[];
  completeTour: (pathname: string) => void;
  /** Let every tour run again from the start (Account settings). */
  resetTours: () => void;
  /** True once the post-signup welcome has been shown and closed. */
  welcomeSeen: boolean;
  markWelcomeSeen: () => void;
  /** Set at signup so the welcome shows once, to new accounts only. */
  pendingWelcome: boolean;
  requestWelcome: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: systemThemeMode(),
      toggleTheme: () =>
        set({ theme: get().theme === "light" ? "dark" : "light" }),
      setTheme: (theme) => set({ theme }),
      onboardingDismissed: false,
      dismissOnboarding: () => set({ onboardingDismissed: true }),
      completedTours: [],
      completeTour: (pathname) =>
        set({ completedTours: [...new Set([...get().completedTours, pathname])] }),
      resetTours: () =>
        set({
          completedTours: [],
          onboardingDismissed: false,
          welcomeSeen: false,
        }),
      welcomeSeen: false,
      markWelcomeSeen: () => set({ welcomeSeen: true, pendingWelcome: false }),
      pendingWelcome: false,
      requestWelcome: () => set({ pendingWelcome: true, welcomeSeen: false }),
    }),
    {
      name: "cloudpanel.ui",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
